import { recommendedCategoriesChanged } from '@map/hooks/occupationCategories';
import { suggestedOccupationGroupsChanged } from '@map/hooks/occupationSuggestionsApi';
import { SkillGroupsData, useSkillGroups } from '@map/hooks/useSkillGroups';
import { ExperienceSkill, SkillGroups } from '@map/types';
import { countBy, groupBy, keyBy, uniqBy } from '@shared/util/array';
import { deepEqual } from '@shared/util/object';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Reducer } from 'redux';
import { createSelector } from 'reselect';
import { ValueOf } from 'type-fest';
import {
  actionCreators as experienceActions,
  experiencePropSelector,
  experiencesSelector,
} from './experiences';

/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
const EXPERIENCE_SKILLS_FETCHED = 'EXPERIENCE_SKILL_FETCHED' as const;
const EXPERIENCE_SKILL_CREATED = 'EXPERIENCE_SKILL_CREATED' as const;
const EXPERIENCE_SKILLS_CREATED = 'EXPERIENCE_SKILLS_CREATED' as const;
const EXPERIENCE_SKILL_UPDATED = 'EXPERIENCE_SKILL_UPDATED' as const;
const EXPERIENCE_SKILLS_UPDATED = 'EXPERIENCE_SKILLS_UPDATED' as const;
/* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

type State = Record<number, ExperienceSkill>;

const invalidateSwrCaches = () => {
  suggestedOccupationGroupsChanged();
  recommendedCategoriesChanged();
};

export const actionCreators = {
  experienceSkillsFetched: (data: ExperienceSkill[]) =>
    ({ type: EXPERIENCE_SKILLS_FETCHED, data }) as const,
  experienceSkillCreated: (data: ExperienceSkill[]) =>
    ({ type: EXPERIENCE_SKILL_CREATED, data }) as const,
  experienceSkillsCreated: (data: ExperienceSkill[]) =>
    ({ type: EXPERIENCE_SKILLS_CREATED, data }) as const,
  experienceSkillUpdated: (data: ExperienceSkill) =>
    ({ type: EXPERIENCE_SKILL_UPDATED, data }) as const,
  experienceSkillsUpdated: (data: ExperienceSkill[]) =>
    ({ type: EXPERIENCE_SKILLS_UPDATED, data }) as const,
};

type Action = ReturnType<
  ValueOf<typeof actionCreators> | typeof experienceActions.experienceDestroyed
>;

const experienceSkills: Reducer<State, Action> = (state = {}, action) => {
  switch (action.type) {
    case EXPERIENCE_SKILLS_FETCHED: {
      const newValue = keyBy(action.data, ({ id }) => id);
      if (deepEqual(state, newValue)) {
        return state;
      }
      return newValue;
    }
    case EXPERIENCE_SKILL_CREATED:
    case EXPERIENCE_SKILL_UPDATED:
      invalidateSwrCaches();
      return { ...state, ...keyBy([action.data].flat(), ({ id }) => id) };
    case EXPERIENCE_SKILLS_UPDATED:
    case EXPERIENCE_SKILLS_CREATED: {
      invalidateSwrCaches();
      return { ...state, ...keyBy(action.data.flat(), ({ id }) => id) };
    }
    case 'EXPERIENCE_DESTROYED': {
      invalidateSwrCaches();
      const experienceId = action.data.id;
      const newState = Object.fromEntries(
        Object.entries(state).filter(([, es]) => es.experience_id !== experienceId),
      );
      return newState;
    }
  }
  return state;
};

export default experienceSkills;

// SELECTORS

const experienceSkillsSelector = (state: { experienceSkills: State }) => state.experienceSkills;

export const experienceSkillsArraySelector = createSelector(experienceSkillsSelector, (hash) =>
  Object.values(hash),
);

export const acceptedExperienceSkillsSelector = createSelector(
  experienceSkillsArraySelector,
  (values) => values.filter((expSkill) => expSkill.choice !== 'never'),
);

const uniqueAcceptedExperienceSkillsSelector = createSelector(
  acceptedExperienceSkillsSelector,
  (values) => uniqBy(values, ({ skill_id: id }) => id),
);

const experienceSkillsByExperienceSelector = createSelector(
  experienceSkillsArraySelector,
  (values) => groupBy(values, ({ experience_id: id }) => id),
);
const acceptedSkillsSelector = createSelector(uniqueAcceptedExperienceSkillsSelector, (skills) =>
  keyBy(skills, (skill) => skill.id),
);

export const useSkills = () => useSelector(acceptedSkillsSelector);
const experienceItemSkillsSelector = createSelector(acceptedExperienceSkillsSelector, (skills) =>
  groupBy(skills, ({ experience_id: id }) => id),
);

const skillCountsByExperienceSelector = createSelector(acceptedExperienceSkillsSelector, (skills) =>
  countBy(skills, ({ experience_id: id }) => id),
);

export const chosenSkillsForExperienceSelector = createSelector(
  experiencePropSelector,
  experienceSkillsArraySelector,
  (exp, values) => values.filter((chosen) => chosen.experience_id === exp.id),
);

export const useAllExperienceSkills = () => useSelector(experienceSkillsArraySelector);

export const useSkillCountsByExperience = () => useSelector(skillCountsByExperienceSelector);

export const useExperienceSkills = (id: number = 0) =>
  useSelector(experienceItemSkillsSelector)[id] ?? [];

export const useGetExperienceSkills = () => {
  const experienceItemSkills = useSelector(experienceItemSkillsSelector);
  return (id: number = 0) => experienceItemSkills[id] ?? [];
};
const hasAddedSkills = createSelector(
  acceptedExperienceSkillsSelector,
  (skills) => skills.length > 0,
);

export const useHasSkills = () => useSelector(hasAddedSkills);

export const useMentorshipSkills = () =>
  useSelector(acceptedExperienceSkillsSelector)
    .filter((skill) => skill.is_mentorship_skill)
    .reduce((acc: ExperienceSkill[], current) => {
      const existingItem = acc.find((item) => item.skill_id === current.skill_id);
      if (!existingItem) {
        acc.push(current);
      }
      return acc;
    }, []);

/**
 * !Only use this for checking conditions, the value is not a count of unique skills!
 */
export const useAcceptedSkillCount = () => useSelector(acceptedExperienceSkillsSelector).length;

export const experiencesWithSkillCountSelector = createSelector(
  experiencesSelector,
  skillCountsByExperienceSelector,
  (experiences, counts) => {
    return Object.values(experiences).map((exp) => ({ ...exp, skillCount: counts[exp.id] || 0 }));
  },
);
export const useExperiencesWithSkillCounts = () => useSelector(experiencesWithSkillCountSelector);

export const getSortedListOfValidSkillsByGroup = (
  skills: ExperienceSkill[],
  skillGroups: SkillGroupsData,
) => {
  const byGroup = Object.entries(groupBy(skills, ({ skill_category: s }) => s || '')).sort(
    (a, b) => b[1].length - a[1].length,
  );

  const result = new Map<SkillGroups, ExperienceSkill[]>();

  for (const [group, items] of byGroup) {
    if (group in skillGroups) {
      result.set(group as SkillGroups, items);
    }
  }

  return Object.fromEntries(result);
};

export const useAcceptedSkillsByGroup = () => {
  const [skillGroups = {}] = useSkillGroups();
  const experiencesSkills = useSelector(uniqueAcceptedExperienceSkillsSelector);
  return useMemo(
    () => getSortedListOfValidSkillsByGroup(experiencesSkills, skillGroups),
    [experiencesSkills, skillGroups],
  );
};

export const useExperienceSkillsByGroups = (id: number) => {
  const skills = useSelector(experienceSkillsByExperienceSelector)[id];
  const [skillGroups = {}] = useSkillGroups();

  return useMemo(
    () =>
      getSortedListOfValidSkillsByGroup(
        skills?.filter(({ choice }) => choice !== 'never') || [],
        skillGroups,
      ),
    [skillGroups, skills],
  );
};
